import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FiEye } from "react-icons/fi";
import { FiEyeOff } from "react-icons/fi"; 
import useForm from "../../hooks/useForm";
import { useThunk } from "../../hooks/useThunk";
import { COMMON_FIELD_TYPES } from "../../constants/fieldTypes";
import { roles } from "../../constants/commonConstants";
import { closeSignInModal, signin, startDataRefresh } from "../../store";

import CustomInput from "../Form/Input";
import CustomButton from "../Form/CustomButton";
import showToast from "../../utils/toastNotifications";

const SignInForm = ({ isModel = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // const { role } = useSelector((state) => state.auth);
  const [showPassword, setShowPassword] = useState(false);

  const [doSignin, isSigninIn, signinError] = useThunk(signin);

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    setFormData,
    clearError,
  } = useForm(
    {
      email: "",
      password: "",
      rememberMe: false,
    },
    {
      email: true,
      password: true,
      rememberMe: false,
    },
    {
      email: COMMON_FIELD_TYPES.EMAIL_FIELD,
      password: COMMON_FIELD_TYPES.REQUIRED_FIELD, 
      rememberMe: COMMON_FIELD_TYPES.BOOLEAN_FIELD,
    }
  );

  const handleSubmit = async (e) => {
    
    e.preventDefault();

    setTouched({
      email: true,
      password: true,
      rememberMe: true,
    });

    const { rememberMe, ...otherData } = formData;
    if (validateForm()) {
      const result = await doSignin(otherData);
      if (result?.success) {
        // Check if user has USER role
        const userRole = result?.response?.data?.userData?.roles;
        if (userRole !== "USER") {
          showToast("error", "Access denied. Only users can login.");
          return;
        }

        showToast("success", "Login Sucessfull");
        // Start the data refresh process
        if (isModel) {
          dispatch(startDataRefresh());
        }
        localStorage.setItem("token", result?.response?.data?.token);

        if (!isModel) {
          navigate("/landlords/manage-property");
        } else {
          // Instead of reloading, close the modal and force component updates
          dispatch(closeSignInModal());

          // Force pending API requests to cancel and retry with new auth token
          // This could be a custom event that your API interceptors listen for
          window.dispatchEvent(new CustomEvent("auth-state-changed"));
        }
      } else {
        showToast("error", result?.error?.message);
      }
    }
  };

  return (
    <div
      className={`relative w-full max-w-[512px] my-auto flex flex-col ${
        isModel ? "w-[440px]" : "w-full"
      } `}
    >
      <h1 className="text-[26px] font-semibold sm:text-[30px] text-[#4772F1] mt-[-10px] font-alexandria">
        Sign In
      </h1>

      <form className="">
        <div className="flex flex-col gap-3 mt-[24px] sm:mt-[40px]">
          <CustomInput
            label={"E-mail Address"}
            type="text"
            name="email"
            value={formData.email}
            error={touched.email && errors.email}
            onChange={(e) => {
              const email = e.target.value.toLowerCase();
              handleInputChange("email", email);
            }}
            onBlur={() => handleBlur("email", formData.email)}
            onFocus={() => clearError("email")}
            placeholder="Enter your email address"
            className="w-full h-[54px] px-3 rounded-lg border border-gray-300 bg-primary-light/30 text-gray-600"
            labelStyle="font-alexandria font-medium  sm:text-[16px] text-dark"
            inputStyle="bg-[#4772F1] bg-opacity-[0.03]"
            isRequired={true}
          />

        <div className="relative">
          <CustomInput
            label="Password"
            type={showPassword ? "text" : "password"}
            name="password"
            value={formData.password}
            error={touched.password && errors.password}
            onChange={(e) => handleInputChange("password", e.target.value)}
            onBlur={() => handleBlur("password", formData.password)}
            onFocus={() => clearError("password")}
            placeholder="Enter your password"
            className="w-full h-[54px] px-3 rounded-lg border border-gray-300 bg-primary-light/30 text-gray-600"
            labelStyle="font-alexandria font-medium sm:text-[16px] text-dark"
            inputStyle="bg-[#4772F1] bg-opacity-[0.03]"
            isRequired={true}
          />
            <span
              className="absolute right-3 top-14 transform -translate-y-1/2 cursor-pointer"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <FiEye />
              ) : (
                <FiEyeOff />
              )}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between mt-[6px] sm:mt-8">
          <label className="flex items-center gap-3 text-[#718096] font-inter text-[12px] sm:text-[14px]">
            <input
              type="checkbox"
              name="rememberMe"
              checked={formData.rememberMe}
              onChange={(e) =>
                handleInputChange("rememberMe", e.target.checked)
              }
              className="w-4 h-4 accent-[#4772F1]"
            />
            <span>Remember me</span>
          </label>
          <Link
            to="/forgot-password"
            className="font-medium underline text-[#4772F1] font-inter text-[12px] sm:text-[14px]"
          >
            Forgot Password?
          </Link>
        </div>

        <div
          className={` flex flex-col gap-2 ${
            isModel ? "mt-[30px] md:mt-[60px]" : "mt-[30px] lg:mt-[70px]"
          } `}
        >
          <CustomButton
            type="submit"
            className="h-[50px] sm:h-[60px] bg-gradient-to-b from-[#4772F1] to-[#873FE2] text-[#F7FAFC] font-alexandria font-bold text-[20px] leading-[28px] rounded-xl"
            buttonText="Sign In"
            onClick={handleSubmit}
            loading={isSigninIn}
            disabled={isSigninIn}
          />

          <div className="flex justify-center">
            <p className="text-[#00000080] font-inter sm:text-[14px] text-[12px]">
              Don't have an account?{" "}
              <Link
                to={"/signup"}
                className="underline cursor-pointer text-[#4772F1]"
              >
                Create an Account
              </Link>
            </p>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SignInForm;
